import { datadogRum } from "@datadog/browser-rum";

import {
  getAdonisTopRecommendationsId,
  getGiftCards,
  getPlatform,
  getPlatformTheme,
  getPromotions,
  getRetailproductsAreRetailProductsVisible,
  getShop,
  getShopCategories,
  getShopCustomCategoriesListings,
  getSkindropIsVisibleOnMarketplace,
  getUser,
} from "api/methods";
import { STALE_TIME } from "api/staleTimes";
import { fetchCatalogLocationId } from "hooks/useCatalogLocationId";
import {
  catalogListingsVerificationQuery,
  type CatalogListingType,
  catalogSummaryQuery,
  needsCatalogListingVerification,
} from "hooks/useCatalogSummary";
import { shopDiscountsQuery } from "hooks/useShopDiscounts";
import { membershipsWithUnlockedQuery, unlockedListingsQuery } from "modules/shopVisibility";
import { queryClient } from "pages/Root";

type CatalogListingsVerificationQuery = ReturnType<typeof catalogListingsVerificationQuery>;

/** Corroboration may restore content, but its outage must not replace an otherwise usable shop. */
export const fetchCatalogListingsVerification = async (
  query: CatalogListingsVerificationQuery,
  listingType: CatalogListingType,
) => {
  try {
    return await queryClient.fetchQuery(query);
  } catch {
    datadogRum.addError(new Error("Catalog listings verification failed."), {
      source: "shop.catalogListingsVerification",
      listingType,
      recovery: "indexed-empty-state",
    });
    return undefined;
  }
};

/**
 * Shared loader function for shop pages (packages, treatments).
 *
 * The listings themselves are read from the Algolia index by the page; what is loaded here is the
 * per-patient overlay (`GET /shop/discounts`, started here and read by the page without waiting), the practice's vocabulary
 * (`GET /shop` for concern and area names), the visibility answers every index query filters on,
 * and one catalog summary (facet counts) for the banners that used to scan `GET /listings`.
 */
export function loadShopCommon(listingType: CatalogListingType) {
  const shopData = queryClient.fetchQuery({ ...getShop.keyed() });
  const theme = queryClient.fetchQuery({ ...getPlatformTheme.keyed() });
  const categories = queryClient.fetchQuery({ ...getShopCategories.keyed() });
  const platform = queryClient.fetchQuery({
    ...getPlatform.keyed(),
    staleTime: STALE_TIME.platformConfig,
  });
  const promotions = queryClient.fetchQuery({ ...getPromotions.keyed() });
  // Badges fill in when this lands; the page does not wait for it.
  void queryClient.prefetchQuery(shopDiscountsQuery());
  const customShopCategories = queryClient.fetchQuery({
    ...getShopCustomCategoriesListings.keyed(),
    staleTime: 1000 * 10,
  });
  const user = queryClient.fetchQuery({
    ...getUser.keyed(),
    staleTime: 1000 * 60 * 2,
  });
  const recommendationIds = queryClient.fetchQuery({
    ...getAdonisTopRecommendationsId.keyed({ limit: 10 }),
    staleTime: 1000 * 30,
  });
  const areRetailProductsVisible = queryClient.fetchQuery({
    ...getRetailproductsAreRetailProductsVisible.keyed(),
    staleTime: 1000 * 10,
  });
  // Skindrop visibility is non-critical; if the endpoint fails, default to false
  const isSkinDropVisible = queryClient
    .fetchQuery({
      ...getSkindropIsVisibleOnMarketplace.keyed(),
      staleTime: 1000 * 10,
      retry: false,
    })
    .catch(() => false);
  const giftCards = queryClient.fetchQuery({ ...getGiftCards.keyed({}) });
  // Unlisted listings the patient reached by link never come back from the index, so the pages list them first.
  const unlockedListings = queryClient.fetchQuery(unlockedListingsQuery());

  const platformLocationId = user.then((userData) => fetchCatalogLocationId(queryClient, userData));

  const catalog = Promise.all([
    platform,
    platformLocationId,
    areRetailProductsVisible,
    isSkinDropVisible,
  ]).then(([platformData, locationId, retailVisible, skinDropVisible]) =>
    queryClient.fetchQuery(
      catalogSummaryQuery({
        platformId: platformData.id,
        platformLocationId: locationId,
        visibility: {
          areRetailProductsVisible: retailVisible,
          isSkinDropVisible: skinDropVisible,
        },
      }),
    ),
  );

  const verifiedListings = Promise.all([platform, platformLocationId, catalog]).then(
    ([platformData, locationId, catalogData]) => {
      if (
        platformData.platformSettings?.treatmentsAndPackagesEnabled === false ||
        !needsCatalogListingVerification(catalogData, listingType)
      ) {
        return undefined;
      }

      const verificationQuery = catalogListingsVerificationQuery({
        platformId: platformData.id,
        platformLocationId: locationId,
      });

      return fetchCatalogListingsVerification(verificationQuery, listingType);
    },
  );

  return {
    platform,
    shopData,
    categories,
    theme,
    promotions,
    customShopCategories,
    user,
    platformLocationId,
    recommendationIds,
    areRetailProductsVisible,
    isSkinDropVisible,
    giftCards,
    unlockedListings,
    catalog,
    verifiedListings,
  };
}

/** Shared loader for the memberships shop PLP (Algolia order, REST detail for cards). */
export function loadShopMemberships() {
  const shopData = queryClient.fetchQuery({ ...getShop.keyed() });
  const categories = queryClient.fetchQuery({ ...getShopCategories.keyed() });
  const platform = queryClient.fetchQuery({
    ...getPlatform.keyed(),
    staleTime: STALE_TIME.platformConfig,
  });
  void queryClient.prefetchQuery(shopDiscountsQuery());
  const customShopCategories = queryClient.fetchQuery({
    ...getShopCustomCategoriesListings.keyed(),
    staleTime: 1000 * 10,
  });
  const user = queryClient.fetchQuery({
    ...getUser.keyed(),
    staleTime: 1000 * 60 * 2,
  });
  const recommendationIds = queryClient.fetchQuery({
    ...getAdonisTopRecommendationsId.keyed({ limit: 10 }),
    staleTime: 1000 * 30,
  });
  const areRetailProductsVisible = queryClient.fetchQuery({
    ...getRetailproductsAreRetailProductsVisible.keyed(),
    staleTime: 1000 * 10,
  });
  const isSkinDropVisible = queryClient
    .fetchQuery({
      ...getSkindropIsVisibleOnMarketplace.keyed(),
      staleTime: 1000 * 10,
      retry: false,
    })
    .catch(() => false);
  const memberships = queryClient.fetchQuery(membershipsWithUnlockedQuery());

  const platformLocationId = user.then((userData) => fetchCatalogLocationId(queryClient, userData));

  const catalog = Promise.all([
    platform,
    platformLocationId,
    areRetailProductsVisible,
    isSkinDropVisible,
  ]).then(([platformData, locationId, retailVisible, skinDropVisible]) =>
    queryClient.fetchQuery(
      catalogSummaryQuery({
        platformId: platformData.id,
        platformLocationId: locationId,
        visibility: {
          areRetailProductsVisible: retailVisible,
          isSkinDropVisible: skinDropVisible,
        },
      }),
    ),
  );

  return {
    platform,
    shopData,
    categories,
    customShopCategories,
    user,
    platformLocationId,
    recommendationIds,
    areRetailProductsVisible,
    isSkinDropVisible,
    memberships,
    catalog,
  };
}
